import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Mail, Trash2, Copy, ChevronRight, Crown, MessageCircle } from 'lucide-react';
import { storage } from '../../lib/storage';
import { toast } from 'sonner';

const SUPPORT_EMAIL = 'brahmastraai20@gmail.com';

export const SettingsModal = ({ open, onOpenChange, theme, onToggleTheme, onOpenPremium }) => {
  const [type, setType] = useState('feedback'); // feedback | refund | suggestion
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const sendMail = () => {
    if (!body.trim()) {
      toast.error('Message khali hai');
      return;
    }
    const labelMap = {
      feedback: 'Feedback',
      refund: 'Refund / Dispute',
      suggestion: 'Suggestion',
    };
    const finalSubject = subject.trim()
      ? `[Brahmastra AI · ${labelMap[type]}] ${subject.trim()}`
      : `[Brahmastra AI · ${labelMap[type]}]`;
    const finalBody = `Type: ${labelMap[type]}\n\n${body.trim()}\n\n---\nSent from Brahmastra AI Settings`;
    const url = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(finalSubject)}&body=${encodeURIComponent(finalBody)}`;
    window.location.href = url;
    toast.success('Email app open ho raha hai', {
      description: `${SUPPORT_EMAIL} pe message draft ready hai`,
    });
    setSubject('');
    setBody('');
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      toast.success('Email copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const clearAllData = () => {
    if (!window.confirm('Sab data delete ho jayega (chats, notes, XP, prime). Continue?')) return;
    Object.values(storage.KEYS).forEach(k => storage.remove(k));
    storage.remove('brahmastra.usage.v1');
    toast.success('All local data cleared');
    setTimeout(() => window.location.reload(), 600);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card border-border max-h-[85vh] overflow-y-auto thin-scroll" data-testid="settings-modal">
        <DialogHeader>
          <DialogTitle className="text-[16px]">Settings</DialogTitle>
          <DialogDescription className="text-[12px] text-muted-foreground">
            Personalize Brahmastra and reach out to support.
          </DialogDescription>
        </DialogHeader>

        {/* Theme */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/40 border border-border">
          <div>
            <div className="text-[13px] font-medium">Dark mode</div>
            <div className="text-[11px] text-muted-foreground">Premium dark aesthetic</div>
          </div>
          <Switch checked={theme === 'dark'} onCheckedChange={onToggleTheme} data-testid="settings-theme-switch" />
        </div>

        {/* Prime quick CTA */}
        <button
          onClick={onOpenPremium}
          data-testid="settings-prime-btn"
          className="w-full flex items-center justify-between gap-2 p-3 rounded-lg bg-gradient-to-r from-mint/10 to-bblue/10 border border-mint/30 hover:border-mint/60 transition"
        >
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-mint" />
            <div className="text-left">
              <div className="text-[13px] font-medium">Brahmastra Prime</div>
              <div className="text-[11px] text-muted-foreground">Upgrade plans · ₹50 / ₹99</div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>

        {/* Contact & Support */}
        <div className="p-3 rounded-lg bg-secondary/40 border border-border space-y-3" data-testid="contact-support">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-bblue" />
              <span className="text-[13px] font-medium">Contact & Support</span>
            </div>
            <button
              onClick={copyEmail}
              data-testid="copy-email-btn"
              className="text-[10.5px] flex items-center gap-1 text-muted-foreground hover:text-bblue transition"
            >
              <Copy className="w-3 h-3" /> {SUPPORT_EMAIL}
            </button>
          </div>

          {/* Type tabs */}
          <div className="flex gap-1 p-0.5 rounded-lg bg-background/60 border border-border">
            {[
              { id: 'feedback', label: 'Feedback' },
              { id: 'suggestion', label: 'Suggestion' },
              { id: 'refund', label: 'Refund' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setType(t.id)}
                data-testid={`contact-type-${t.id}`}
                className={`flex-1 text-[11.5px] py-1.5 rounded-md transition ${
                  type === t.id
                    ? 'bg-card border border-border text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (optional)"
            data-testid="contact-subject"
            className="bg-background/60 border-border h-9 text-[13px]"
          />
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              type === 'refund'
                ? 'Apna payment ID + reason likhein...'
                : type === 'suggestion'
                ? 'Naya feature, improvement, ya kuch idea share karein...'
                : 'Apni baat yahan likhein...'
            }
            rows={4}
            data-testid="contact-body"
            className="bg-background/60 border-border resize-none text-[13px]"
          />
          <Button
            onClick={sendMail}
            className="w-full bg-bblue text-[#001a26] hover:brightness-110"
            data-testid="contact-send-btn"
          >
            <Mail className="w-4 h-4 mr-1.5" /> Send to {SUPPORT_EMAIL}
          </Button>
          <div className="text-[10px] text-muted-foreground text-center">
            Yeh aapke device ka email app open karega prefilled message ke saath.
          </div>
        </div>

        {/* Danger zone */}
        <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/20">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[12.5px] font-medium text-destructive">Clear all local data</div>
              <div className="text-[10.5px] text-muted-foreground">Chats, notes, XP, prime — sab kuch wipe.</div>
            </div>
            <Button variant="destructive" size="sm" onClick={clearAllData} data-testid="clear-data-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          </div>
        </div>

        <div className="text-center text-[10px] text-muted-foreground">
          Brahmastra AI · v1.0 · Made for UGC NET aspirants
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
